import React, { useEffect, useRef, useState } from "react";
import VideoThumbnail from "react-video-thumbnail";
import {
  Box,
  Button,
  CardMedia,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useForkRef,
} from "@mui/material";
import ReactPlayer from "react-player";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CropFreeIcon from "@mui/icons-material/CropFree";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import api from "../../../conf/api";
import { toast } from "react-toastify";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Cropper from "react-easy-crop";
import { RiseLoader } from "react-spinners";
import VrpanoIcon from "@mui/icons-material/Vrpano";
import { convertLength } from "@mui/material/styles/cssUtils";

// utils/cropImage.js
function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = "anonymous"; // prevent CORS issues

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const radians = rotation * (Math.PI / 180);

      // Calculate bounding box of rotated image
      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));
      const newWidth = image.width * cos + image.height * sin;
      const newHeight = image.width * sin + image.height * cos;

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.translate(-pixelCrop.x, -pixelCrop.y);
      ctx.translate(newWidth / 2, newHeight / 2);
      ctx.rotate(radians);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result);
      }, "image/png");
    };

    image.onerror = (err) => reject(err);
  });
}

export async function compressBase64Image(base64, maxWidth = 480) {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
  });
}

function ThumbnailCreatorDialog({
  open,
  setOpen,
  video,
  refreshFunc = () => {},
}) {
  console.log(video);
  const [currentTime, setCurrentTime] = useState(0);
  const playerWrapperRef = useRef(null);
  const [thumbnail, setThumbnail] = useState(null);
  // The raw frame "Get Landscape" converts from — kept separate from
  // `thumbnail` so switching Black/Blur after a first conversion re-runs on
  // the original frame instead of on the already-landscaped result (which
  // is already full-canvas, so re-converting it is a no-op either way).
  const originalFrameRef = useRef(null);
  const [loadingImg, setImgLoading] = React.useState(false);
  const [uploading, setUploading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(9 / 16);
  const ObjectViews = [
    "contain",
    "vertical-cover",
    "cover",
    "horizontal-cover",
  ];
  const [objectView, setObjectView] = useState("contain");
  const [landscapeConversion, setLandscapeConversion] = useState({
    loading: false,
    fromServer: false,
    base64Img: null,
  });
  const [landscapeFillMode, setLandscapeFillMode] = useState("black");

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  const handleUpload = async () => {
    if (!thumbnail || !video?.id) return;
    setUploading(true);
    try {
      const croppedBase64 = await getCroppedImg(
        thumbnail,
        croppedAreaPixels,
        rotation,
      );
      const base64 = croppedBase64.split(",")[1];

      const response = await api.post(
        "/videos/upload-thumbnail/",
        {
          image_base64: base64,
          video_id: video.id,
        },
        { timeout: 3 * 1000 * 60 },
      );

      toast.success("Upload success:", response.data);
      refreshFunc();
      setOpen(false);
    } catch (error) {
      console.error("Upload error:", error.response?.data || error.message);
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };
  const handleLandscapeConversion = async () => {
    const player = playerWrapperRef.current;
    if (player && player.player?.isPlaying) {
      toast.error("Stop the player to set frame.");
      return;
    }
    setLandscapeConversion((pre) => ({ ...pre, loading: true }));
    try {
      // Always convert from the original frame, not from `thumbnail` — once
      // converted once, `thumbnail` is already a full canvas, and
      // re-running on that is a no-op regardless of fill_mode.
      const sourceFrame = originalFrameRef.current || thumbnail;
      const response = await api.post(
        "videos/portrait-to-landscape/",
        {
          thumbnail: sourceFrame,
          fill_mode: landscapeFillMode,
        },
        { timeout: 10 * 60 * 1000 },
      );
      if (!originalFrameRef.current) originalFrameRef.current = sourceFrame;
      setThumbnail(response.data.data_uri);
    } catch (error) {
      toast.error("Conversion failed:", error);
    } finally {
      setLandscapeConversion((pre) => ({ ...pre, loading: false }));
    }
  };

  const handleRotation = (angle) => () => {
    setRotation((prev) => (prev + angle + 360) % 360);
  };

  return (
    <Modal open={open} onClose={!uploading ? () => setOpen(false) : () => {}}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100vw", sm: "92vw" },
          maxWidth: 1400,
          maxHeight: { xs: "100dvh", sm: "94vh" },
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: { xs: 1, md: 3 },
          borderRadius: { xs: 0, sm: 2 },
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap={2}
        >
          {/* Video Display */}
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }} gutterBottom>
            <AddPhotoAlternateIcon color="success" fontSize="large" />
            Thumbnail Setter
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexDirection="column"
                height="100%"
                gap={1}
              >
                <Typography
                  sx={{ zIndex: 1, mt: 1, textDecoration: "underline" }}
                  endIcon={<CropFreeIcon />}
                  variant="h4"
                >
                  {loadingImg ? "Capturing " : "Captured "} at{" "}
                  {formatTime(currentTime)}
                </Typography>
                <CardMedia
                  component={ReactPlayer}
                  url={
                    video.dropbox_link
                      ? video.dropbox_link?.replace(
                          "www.dropbox.com",
                          "dl.dropboxusercontent.com",
                        )
                      : video.file
                  }
                  controls
                  width="100%"
                  height="auto"
                  ref={playerWrapperRef}
                  onProgress={({ playedSeconds }) =>
                    setCurrentTime(playedSeconds)
                  }
                  sx={{
                    maxHeight: "300px",
                    minHeight: 200,
                    borderRadius: 2,
                    bgcolor: "grey.300",
                    "& video": {
                      maxHeight: "300px !important",
                      borderRadius: 1.25,
                    },
                  }}
                />
              </Box>
            </Grid>
            {/* SS */}
            <Grid container item xs={12} sm={6}>
              <Grid
                item
                container
                spacing={1}
                justifyContent="space-around"
                display="flex"
                alignItems="center"
              >
                <Grid item xs>
                  <Typography variant="h6" sx={{ textDecoration: "underline" }}>
                    Captured Thumbnail
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    sx={{ color: "#fff" }}
                    endIcon={
                      uploading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    onClick={handleUpload}
                    disabled={
                      uploading || !thumbnail || landscapeConversion.loading
                    }
                  >
                    {uploading ? "Uploading..." : "Upload Thumbnail"}
                  </Button>
                </Grid>
                <Grid item>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={landscapeFillMode}
                    onChange={(e, value) => value && setLandscapeFillMode(value)}
                    disabled={landscapeConversion.loading}
                  >
                    <ToggleButton value="black">Black sides</ToggleButton>
                    <ToggleButton value="blur">Blurred sides</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item>
                  <Button
                    disabled={landscapeConversion.loading || !thumbnail}
                    variant="contained"
                    startIcon={
                      landscapeConversion.loading ? (
                        <CircularProgress size={18} color="error" />
                      ) : (
                        <VrpanoIcon />
                      )
                    }
                    onClick={handleLandscapeConversion}
                  >
                    Get Landscape
                  </Button>
                </Grid>
              </Grid>
              {!landscapeConversion.fromServer && (
                <Box hidden>
                  <VideoThumbnail
                    key={currentTime}
                    videoUrl={
                      video.dropbox_link
                        ? video.dropbox_link?.replace(
                            "www.dropbox.com",
                            "dl.dropboxusercontent.com",
                          )
                        : video.file
                    }
                    thumbnailHandler={async (_thumbnail) => {
                      try {
                        setImgLoading(true);
                        setZoom(0);
                        const img = new Image();
                        img.src = _thumbnail;
                        img.onload = () => {
                          setThumbnail(_thumbnail);
                          originalFrameRef.current = _thumbnail;
                          setZoom(1);
                        };
                        img.onerror = () => {
                          toast.error("Failed to load image.");
                        };
                      } catch (err) {
                        toast.error("Failed to process thumbnail");
                        setImgLoading(false);
                      }
                    }}
                    snapshotAtTime={currentTime.toFixed(2)}
                  />
                </Box>
              )}

              <Grid container mt={1} alignItems="center">
                <Grid container item gap={2} xs alignItems="center">
                  <Grid item xs={3} md={2}>
                    <Typography variant="body1">Aspect Ratio:</Typography>
                  </Grid>
                  <Grid item xs container spacing={0.5}>
                    <Grid item>
                      <Button
                        variant={aspect === 4 / 3 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => setAspect(4 / 3)}
                      >
                        4:3
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant={aspect === 16 / 9 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => setAspect(16 / 9)}
                      >
                        16:9
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant={aspect === 9 / 16 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => setAspect(9 / 16)}
                      >
                        9:16
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant={aspect === 3 / 4 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => setAspect(3 / 4)}
                      >
                        3:4
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant={aspect === 1 / 1 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => setAspect(1 / 1)}
                      >
                        1:1
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Box display="flex" justifyContent="center" gap={1}>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={handleRotation(-90)}
                    >
                      <RotateLeftIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={handleRotation(90)}
                    >
                      <RotateRightIcon fontSize="large" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
              <Grid container alignItems={"center"}>
                <Grid item xs={2} md={2}>
                  <Typography variant="body1">Object View:</Typography>
                </Grid>
                <Grid item xs={10}>
                  <FormControl size="small">
                    <RadioGroup
                      row={{ xs: false, md: true }}
                      aria-labelledby="CropView"
                      value={objectView}
                      onChange={(e, new_value) => {
                        setObjectView(new_value);
                      }}
                    >
                      {ObjectViews.map((obv) => (
                        <FormControlLabel
                          key={obv}
                          value={obv}
                          control={<Radio size="small" />}
                          label={obv.toLocaleUpperCase()}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
              {/* Capture & Crop */}
              <Box
                position="relative"
                width="100%"
                height={300}
                borderRadius={2}
                overflow={"hidden"}
                sx={{
                  opacity: loadingImg ? 0 : 1,
                  transition: "opacity 0.5s ease-in",
                }}
              >
                {loadingImg ||
                  (landscapeConversion.loading && (
                    <Box
                      zIndex={2}
                      position="absolute"
                      top={"40%"}
                      left={"40%"}
                    >
                      <RiseLoader color="#ff9595ff" size={25} />
                    </Box>
                  ))}
                <Cropper
                  image={thumbnail}
                  crop={crop}
                  zoom={zoom}
                  maxZoom={1.482}
                  minZoom={1}
                  rotation={rotation}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  objectFit={objectView}
                  onMediaLoaded={() => {
                    setImgLoading(false);
                  }}
                  onCropComplete={(_, croppedPixels) =>
                    setCroppedAreaPixels(croppedPixels)
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
}

export default ThumbnailCreatorDialog;
